import { User as Auth0User } from '@auth0/auth0-react';
import { UserProfile } from './userService';

export interface User {
  _id: string;
  username: string;
  displayName: string;
  email: string;
  profilePicture?: string;
  role: string;
  auth0Id?: string;
  gameStats: {
    totalPoints: number;
    questionsAnswered: number;
    correctAnswers: number;
    streak: number;
    level: number;
    achievements: string[];
    bestStreak: number;
    totalGames: number;
  };
  preferences: {
    difficulty: string;
    categories: string[];
    notifications: boolean;
    theme: string;
  };
}

export interface AuthError {
  status: string;
  message: string;
}

// Flag to prevent multiple simultaneous authentication attempts
let isAuthenticating = false;

const authService = {
  loginWithAuth0: async (auth0User: Auth0User, accessToken: string): Promise<UserProfile> => {
    // Prevent multiple simultaneous authentication attempts
    if (isAuthenticating) {
      console.log('🔄 Authentication already in progress, waiting...');
      // Wait for existing authentication to complete
      while (isAuthenticating) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Return the current user if authentication completed
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        return currentUser;
      }
    }

    isAuthenticating = true;
    try {
      console.log('🔐 Starting Auth0 login for user:', auth0User.email);

      // Use the authenticated endpoint first (this is the primary method)
      const userData = {
        sub: auth0User.sub!,
        email: auth0User.email!,
        name: auth0User.name!,
        nickname: auth0User.nickname,
        picture: auth0User.picture,
      };

      console.log('📤 Sending login request to backend');
      const response = await fetch(`${process.env.REACT_APP_USERS_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Auth endpoint failed:', response.status, errorText);

        // If auth endpoint fails, try the public endpoint as fallback
        console.log('🔄 Trying public user creation as fallback');
        const publicUserData = {
          auth0Id: auth0User.sub!,
          email: auth0User.email!,
          name: auth0User.name!,
          picture: auth0User.picture,
        };

        const publicResponse = await fetch(`${process.env.REACT_APP_USERS_API_URL}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(publicUserData)
        });

        if (!publicResponse.ok) {
          const publicErrorText = await publicResponse.text();
          console.error('❌ Public endpoint also failed:', publicResponse.status, publicErrorText);
          throw new Error(`Both auth methods failed. Auth: ${response.status}, Public: ${publicResponse.status}`);
        }

        const result = await publicResponse.json();
        const userProfile = result.data?.user || result.data || result;
        console.log('✅ User created via public endpoint fallback');

        // Store user data
        sessionStorage.setItem('user', JSON.stringify(userProfile));
        sessionStorage.setItem('auth0User', JSON.stringify(auth0User));
        sessionStorage.setItem('auth0Token', accessToken);

        return userProfile;
      }

        const result = await response.json();
        const userProfile = result.data?.user || result.data || result;
        console.log('✅ User authenticated and synced successfully');

      // Store user data
      sessionStorage.setItem('user', JSON.stringify(userProfile));
      sessionStorage.setItem('auth0User', JSON.stringify(auth0User));
      sessionStorage.setItem('auth0Token', accessToken);

      return userProfile;
    } catch (error) {
      console.error('💥 Login error:', error);
      throw error;
    } finally {
      isAuthenticating = false;
    }
  },
  
  getProfile: async (): Promise<UserProfile> => {
    try {
      const accessToken = sessionStorage.getItem('auth0Token');
      if (!accessToken) {
        throw new Error('No authentication token found');
      }

      console.log('🔍 Fetching user profile from backend...');

      // Get profile from backend with Auth0 token
      const response = await fetch(`${process.env.REACT_APP_USERS_API_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Profile fetch failed: ${response.status} ${response.statusText}`);
        console.error('❌ Error response:', errorText);

        if (response.status === 404) {
          throw new Error('User profile not found - user may not exist in database');
        } else if (response.status === 401) {
          throw new Error('Authentication failed - invalid or expired token');
        } else {
          throw new Error(`Failed to get profile: ${response.status} ${errorText}`);
        }
      }

      const result = await response.json();
      const userProfile = result.data?.user || result.data || result;

      console.log('✅ Profile fetched successfully:', userProfile.email);

      // Update sessionStorage with latest data
      sessionStorage.setItem('user', JSON.stringify(userProfile));

      return userProfile;
    } catch (error) {
      console.error('💥 getProfile error:', error);
      throw error;
    }
  },
  
  updateProfile: async (profileData: Partial<UserProfile>): Promise<UserProfile> => {
    try {
      const accessToken = sessionStorage.getItem('auth0Token');
      if (!accessToken) {
        throw new Error('No authentication token found');
      }
      
      // Update profile via backend with Auth0 token
      const response = await fetch(`${process.env.REACT_APP_USERS_API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const result = await response.json();
      const updatedProfile = result.data?.user || result.data || result;
      
      // Update sessionStorage
      sessionStorage.setItem('user', JSON.stringify(updatedProfile));
      
      return updatedProfile;
    } catch (error) {
      throw error;
    }
  },
  
  logout: (): void => {
    // Clear all session data
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('auth0User');
    sessionStorage.removeItem('auth0Token');
    // Also clear localStorage for backwards compatibility
    localStorage.removeItem('user');
    localStorage.removeItem('auth0User');
    // Don't redirect automatically - let Auth0 handle this
  },

  // Clear session data (useful for debugging auth issues)
  clearSession: (): void => {
    console.log('🧹 Clearing session data');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('auth0User');
    sessionStorage.removeItem('auth0Token');
    localStorage.removeItem('user');
    localStorage.removeItem('auth0User');
  },
  
  isAuthenticated: (): boolean => {
    return !!sessionStorage.getItem('auth0Token');
  },
  
  getCurrentUser: (): UserProfile | null => {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr) as UserProfile;
    } catch (e) {
      return null;
    }
  },

  // Get Auth0 access token
  getAccessToken: (): string | null => {
    return sessionStorage.getItem('auth0Token');
  },

  // Get Auth0 user data
  getAuth0User: (): Auth0User | null => {
    const auth0UserStr = sessionStorage.getItem('auth0User');
    if (!auth0UserStr) return null;
    
    try {
      return JSON.parse(auth0UserStr) as Auth0User;
    } catch (e) {
      return null;
    }
  },

  // Token refresh handler
  refreshToken: async (getAccessTokenSilently: () => Promise<string>): Promise<string> => {
    try {
      const newToken = await getAccessTokenSilently();
      sessionStorage.setItem('auth0Token', newToken);
      return newToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw error;
    }
  }
};

export default authService;